import React, { useState, useEffect } from 'react'
import { KTCard, KTCardBody } from '../../../../_metronic/helpers'
import { KTIcon } from '../../../../_metronic/helpers'
import { validateImage } from '../../../helper-functions/ImageValidation'

// Local Platform type for edit modal
interface PlatformItem {
  id: number
  platform_name: string
  platform_url: string
  platform_logo: string | null
  description: string
  created_at: string
  updated_at: string
  status: number
}

interface EditPlatformProps {
  platform: PlatformItem
  onClose: () => void
  onSave: (platform: PlatformItem, logoFile?: File | null) => void
}

const EditPlatform: React.FC<EditPlatformProps> = ({ platform, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    platform_name: platform.platform_name,
    platform_url: platform.platform_url,
    description: platform.description,
    status: platform.status
  })
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    setFormData({
      platform_name: platform.platform_name,
      platform_url: platform.platform_url,
      description: platform.description,
      status: platform.status
    })
    // Set initial logo preview
    if (platform.platform_logo) {
      // Helper function to get full logo URL
      const getLogoUrl = (logo: string | null): string | null => {
        if (!logo) return null
        
        // If already a full URL (starts with http:// or https://), return as is
        if (logo.startsWith('http://') || logo.startsWith('https://')) {
          return logo
        }
        
        // Otherwise, prepend API base URL
        let baseURL = process.env.REACT_APP_API_URL || ''
        if (!baseURL) return logo
        
        // Remove /api from the end of baseURL if present
        baseURL = baseURL.replace(/\/api\/?$/, '')
        
        // Remove trailing slash from baseURL and leading slash from logo
        const cleanBaseURL = baseURL.replace(/\/$/, '')
        const cleanLogo = logo.replace(/^\//, '')
        
        return `${cleanBaseURL}/${cleanLogo}`
      }
      setLogoPreview(getLogoUrl(platform.platform_logo))
    } else {
      setLogoPreview(null)
    }
    setLogoFile(null)
  }, [platform])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.platform_name.trim()) {
      newErrors.platform_name = 'Platform name is required'
    }

    if (!formData.platform_url.trim()) {
      newErrors.platform_url = 'Platform URL is required'
    } else {
      try {
        new URL(formData.platform_url)
      } catch {
        newErrors.platform_url = 'Please enter a valid URL'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (validateForm()) {
      onSave({
        ...platform,
        platform_name: formData.platform_name.trim(),
        platform_url: formData.platform_url.trim(),
        description: formData.description.trim(),
        status: formData.status
      }, logoFile)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    logoType: string,
    MAX_FILE_SIZE: number,
    ALLOWED_TYPES: string[],
    MAX_DIMENSIONS: { [key: string]: { width: number; height: number } }
  ) => {
    const newFile = e.target.files?.[0]
    if (newFile) {
      validateImage(
        newFile,
        logoType,
        (validFileUrl) => {
          setLogoFile(newFile)
          setLogoPreview(validFileUrl)
        },
        MAX_FILE_SIZE,
        ALLOWED_TYPES,
        MAX_DIMENSIONS
      )
    }
  }

  const MAX_FILE_SIZE = 2 // MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
  const MAX_DIMENSIONS = { platform_logo: { width: 500, height: 500 } }

  return (
    <div className='modal fade show d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className='modal-dialog modal-lg'>
        <div className='modal-content'>
          <KTCard>
            <div className='card-header'>
              <div className='card-title'>
                <h3 className='fw-bold text-dark'>Edit Platform</h3>
              </div>
              <div className='card-toolbar'>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={onClose}
                >
                  <KTIcon iconName='cross' className='fs-1' />
                </button>
              </div>
            </div>
            <KTCardBody>
              <form onSubmit={handleSubmit}>
                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <label className='col-form-label fw-bold fs-6'>
                      <span className='text-danger'>*</span>Platform Name
                    </label>
                    <input
                      type='text'
                      name='platform_name'
                      className={`form-control form-control-solid ${errors.platform_name ? 'is-invalid' : ''}`}
                      placeholder='Enter platform name'
                      value={formData.platform_name}
                      onChange={handleChange}
                    />
                    {errors.platform_name && <div className='invalid-feedback'>{errors.platform_name}</div>}
                  </div>
                  <div className='col-lg-6'>
                    <label className='col-form-label fw-bold fs-6'>Status</label>
                    <select
                      name='status'
                      className='form-select form-select-solid'
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-12'>
                    <label className='col-form-label fw-bold fs-6'>
                      <span className='text-danger'>*</span>Platform URL
                    </label>
                    <input
                      type='url'
                      name='platform_url'
                      className={`form-control form-control-solid ${errors.platform_url ? 'is-invalid' : ''}`}
                      placeholder='https://example.com'
                      value={formData.platform_url}
                      onChange={handleChange}
                    />
                    {errors.platform_url && <div className='invalid-feedback'>{errors.platform_url}</div>}
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-12'>
                    <label className='col-form-label fw-bold fs-6'>Platform Logo</label>
                    <div className='d-flex align-items-center gap-3'>
                      {logoPreview && (
                        <div className='symbol symbol-100px'>
                          <img src={logoPreview} alt='Logo preview' className='w-100' />
                        </div>
                      )}
                      <div className='flex-grow-1'>
                        <input
                          type='file'
                          accept='image/jpeg,image/jpg,image/png,image/gif'
                          className='form-control form-control-solid'
                          onChange={(e) => handleImageChange(e, 'platform_logo', MAX_FILE_SIZE, ALLOWED_TYPES, MAX_DIMENSIONS)}
                        />
                        <div className='form-text'>Max size: 2MB, Max dimensions: 500x500px</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-12'>
                    <label className='col-form-label fw-bold fs-6'>
                      Description
                    </label>
                    <textarea
                      name='description'
                      className={`form-control form-control-solid ${errors.description ? 'is-invalid' : ''}`}
                      rows={3}
                      placeholder='Enter platform description'
                      value={formData.description}
                      onChange={handleChange}
                    />
                    {errors.description && <div className='invalid-feedback'>{errors.description}</div>}
                  </div>
                </div>


                <div className='d-flex justify-content-end'>
                  <button
                    type='button'
                    className='btn btn-light me-3'
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                  >
                    <KTIcon iconName='check' className='fs-2' />
                    Update Platform
                  </button>
                </div>
              </form>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    </div>
  )
}

export { EditPlatform }

